import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPageDetail } from '../api/seoTracking';

type CardColor = 'red' | 'amber' | 'gray' | 'blue' | 'green' | 'orange';

const CARD_STYLES: Record<CardColor, { border: string; bg: string; count: string; label: string }> = {
  red:    { border: 'border-red-200',    bg: 'bg-red-50',    count: 'text-red-700',    label: 'text-red-900' },
  amber:  { border: 'border-amber-200',  bg: 'bg-amber-50',  count: 'text-amber-700',  label: 'text-amber-900' },
  gray:   { border: 'border-gray-200',   bg: 'bg-gray-50',   count: 'text-gray-600',   label: 'text-gray-800' },
  blue:   { border: 'border-blue-200',   bg: 'bg-blue-50',   count: 'text-blue-700',   label: 'text-blue-900' },
  green:  { border: 'border-green-200',  bg: 'bg-green-50',  count: 'text-green-700',  label: 'text-green-900' },
  orange: { border: 'border-orange-200', bg: 'bg-orange-50', count: 'text-orange-700', label: 'text-orange-900' },
};

function StatusCard({ count, label, hint, color }: { count: number; label: string; hint: string; color: CardColor }) {
  const s = CARD_STYLES[color];
  const muted = count === 0;
  return (
    <div className={`rounded-lg border ${muted ? 'border-gray-200 bg-white' : s.border + ' ' + s.bg} px-3 py-2`}>
      <div className={`text-2xl font-semibold tabular-nums ${muted ? 'text-gray-300' : s.count}`}>{count}</div>
      <div className={`text-xs font-semibold uppercase ${muted ? 'text-gray-400' : s.label}`}>{label}</div>
      <div className="mt-0.5 text-[10px] leading-tight text-gray-500">{hint}</div>
    </div>
  );
}

function ClosedBreakdown({ improved, worsened, neutral, other }: { improved: number; worsened: number; neutral: number; other: number }) {
  const total = improved + worsened + neutral + other;
  if (total === 0) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs">
      <span className="font-semibold uppercase text-gray-500">Closed outcomes:</span>
      {improved > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 font-semibold text-green-700">
          <span>✓</span> Improved <span className="tabular-nums">{improved}</span>
        </span>
      )}
      {worsened > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-700">
          <span>✗</span> Worsened <span className="tabular-nums">{worsened}</span>
        </span>
      )}
      {neutral > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 font-semibold text-gray-700">
          <span>·</span> Neutral <span className="tabular-nums">{neutral}</span>
        </span>
      )}
      {other > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-500">
          <span>↩</span> Closed (no measurement) <span className="tabular-nums">{other}</span>
        </span>
      )}
    </div>
  );
}

export function SeoTrackingDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['seo-tracking-detail', id],
    queryFn: () => fetchPageDetail(id),
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto" onClick={onClose}>
      <div className="my-8 w-full max-w-5xl rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-gray-900">{data?.title || id}</h2>
            <p className="mt-0.5 truncate text-xs text-gray-500">
              {data?.url} · {data?.section} · {data?.locale}
              {' · '}
              <a
                href={`https://github.com/blockchain-web-services/bws-api-telegram-xbot/blob/prod/${data?.sources.audit}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:underline"
              >
                AUDIT.md
              </a>
              {' · '}
              <a
                href={`https://github.com/blockchain-web-services/bws-api-telegram-xbot/blob/prod/${data?.sources.todo}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:underline"
              >
                TODO.md
              </a>
            </p>
            {(data?.createdDate || data?.updatedDate) && (
              <p className="mt-0.5 text-[11px] text-gray-400">
                {data.createdDate && (
                  <>
                    <span title={`source: ${data.createdSource || 'unknown'}`}>Created {data.createdDate}</span>
                  </>
                )}
                {data.updatedDate && data.updatedDate !== data.createdDate && (
                  <>
                    {data.createdDate && <> · </>}
                    <span title={`source: ${data.updatedSource || 'unknown'}`}>Updated {data.updatedDate}</span>
                  </>
                )}
                {data.lastAuditDate && <> · Audit refreshed {data.lastAuditDate}</>}
              </p>
            )}
          </div>
          <button onClick={onClose} className="ml-4 rounded-md p-1 text-gray-400 hover:text-gray-600">
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" /></svg>
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
          </div>
        )}
        {error && (
          <div className="m-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Failed to load page detail.
          </div>
        )}
        {data && (
          <div className="space-y-6 p-6">
            {/* GSC history */}
            <section>
              <h3 className="mb-2 text-sm font-semibold uppercase text-gray-500">GSC performance ({data.gscHistory.length} weeks)</h3>
              {data.gscHistory.length === 0 ? (
                <p className="text-sm text-gray-400">No GSC data yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-gray-200 text-xs uppercase text-gray-400">
                      <tr><th className="px-2 py-1 text-left">Week</th><th className="px-2 py-1 text-right">Clicks</th><th className="px-2 py-1 text-right">Imp</th><th className="px-2 py-1 text-right">CTR</th><th className="px-2 py-1 text-right">Pos</th><th className="px-2 py-1 text-left">Indexing</th></tr>
                    </thead>
                    <tbody>
                      {data.gscHistory.map((g) => (
                        <tr key={g.date} className="border-b border-gray-100 last:border-b-0">
                          <td className="px-2 py-1 text-gray-600">{g.date}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{g.clicks}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{g.impressions}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{(g.ctr * 100).toFixed(2)}%</td>
                          <td className="px-2 py-1 text-right tabular-nums">{g.position.toFixed(1)}</td>
                          <td className="px-2 py-1 text-xs text-gray-500">{g.indexing}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Top queries */}
            {data.topQueries.length > 0 && (
              <section>
                <h3 className="mb-2 text-sm font-semibold uppercase text-gray-500">Top queries (latest week)</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-gray-200 text-xs uppercase text-gray-400">
                      <tr><th className="px-2 py-1 text-left">Query</th><th className="px-2 py-1 text-right">Clicks</th><th className="px-2 py-1 text-right">Imp</th><th className="px-2 py-1 text-right">CTR</th><th className="px-2 py-1 text-right">Pos</th></tr>
                    </thead>
                    <tbody>
                      {data.topQueries.map((q, i) => (
                        <tr key={i} className="border-b border-gray-100 last:border-b-0">
                          <td className="px-2 py-1 text-gray-700">{q.query}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{q.clicks}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{q.impressions}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{(q.ctr * 100).toFixed(2)}%</td>
                          <td className="px-2 py-1 text-right tabular-nums">{q.position.toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* On-page audit */}
            <section>
              <h3 className="mb-2 text-sm font-semibold uppercase text-gray-500">On-page audit</h3>
              <ul className="space-y-1 text-sm">
                {data.onPageChecks.map((c, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className={c.ok ? 'text-green-600' : 'text-red-600'}>{c.ok ? '✓' : '✗'}</span>
                    <span className="font-medium text-gray-700">{c.label}:</span>
                    <span className="text-gray-600">{c.value}</span>
                  </li>
                ))}
              </ul>
              {data.issues.length > 0 && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
                  <h4 className="mb-1 text-xs font-semibold uppercase text-red-700">Identified issues</h4>
                  <ul className="space-y-1 text-sm text-red-700">
                    {data.issues.map((i, idx) => <li key={idx}>· {i}</li>)}
                  </ul>
                </div>
              )}
            </section>

            {/* TODOs */}
            <section>
              <h3 className="mb-3 text-sm font-semibold uppercase text-gray-500">TODOs</h3>

              {/* Status overview — card grid */}
              <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                <StatusCard
                  count={data.todos.open.P0.length}
                  label="P0 open"
                  hint="urgent — do this week"
                  color="red"
                />
                <StatusCard
                  count={data.todos.open.P1.length}
                  label="P1 open"
                  hint="do this month"
                  color="amber"
                />
                <StatusCard
                  count={data.todos.open.P2.length}
                  label="P2 open"
                  hint="lower priority"
                  color="gray"
                />
                <StatusCard
                  count={data.todos.watchList?.length ?? 0}
                  label="Watch list"
                  hint="borderline signals, awaiting check-back date"
                  color="gray"
                />
                <StatusCard
                  count={data.todos.inProgress.length}
                  label="In progress"
                  hint="executed, awaiting outcome"
                  color="blue"
                />
                <StatusCard
                  count={data.todos.needsResuggest?.length ?? 0}
                  label="Needs re-suggest"
                  hint="applier-rejected, next Mon's suggest will rewrite"
                  color="orange"
                />
                <StatusCard
                  count={data.todos.closed.length}
                  label="Closed"
                  hint="resolved + measured"
                  color="green"
                />
              </div>

              {/* Related site-wide TODOs (from seo-todos/TODOS.md) */}
              {data.relatedSiteTodos && data.relatedSiteTodos.length > 0 && (
                <div className="mb-4 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2">
                  <div className="mb-1 text-xs font-semibold uppercase text-purple-700">
                    Linked site-wide TODOs (from seo-todos/TODOS.md)
                  </div>
                  <ul className="space-y-1 text-sm">
                    {data.relatedSiteTodos.map((t, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-semibold ${
                          t.status === 'done' ? 'bg-green-100 text-green-700' :
                          t.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          t.status === 'blocked' ? 'bg-gray-100 text-gray-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>{t.status}</span>
                        <span className="text-gray-700">{t.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Watch list */}
              {data.todos.watchList && data.todos.watchList.length > 0 && (
                <div className="mt-3 rounded-lg border border-gray-300 bg-gray-50 p-3">
                  <h4 className="mb-2 text-xs font-semibold uppercase text-gray-700">Watch list ({data.todos.watchList.length})</h4>
                  <ul className="space-y-3">
                    {data.todos.watchList.map((t, i) => (
                      <li key={i}>
                        <div className="text-sm font-medium text-gray-800">
                          <span className="mr-2 inline-flex rounded bg-gray-200 px-1.5 py-0.5 text-xs font-semibold text-gray-700">{t.priority}{t.failCount > 0 ? ` ×${t.failCount}` : ''}</span>
                          {t.title}
                        </div>
                        {(t.metric || t.promoteIf || t.checkback) && (
                          <div className="mt-1 text-xs text-gray-600">
                            {t.metric && <div>· <strong>Metric:</strong> {t.metric}</div>}
                            {t.promoteIf && <div>· <strong>Promote if:</strong> {t.promoteIf}</div>}
                            {t.checkback && <div>· <strong>Check-back:</strong> {t.checkback}</div>}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Closed-outcomes breakdown (if any) */}
              {data.todos.closed.length > 0 && (
                <ClosedBreakdown
                  improved={data.todos.closed.filter((c) => c.outcome === 'IMPROVED').length}
                  worsened={data.todos.closed.filter((c) => c.outcome === 'WORSENED').length}
                  neutral={data.todos.closed.filter((c) => c.outcome === 'NEUTRAL').length}
                  other={data.todos.closed.filter((c) => !['IMPROVED', 'WORSENED', 'NEUTRAL'].includes(c.outcome)).length}
                />
              )}

              {(['P0', 'P1', 'P2'] as const).map((p) => {
                const items = data.todos.open[p];
                if (items.length === 0) return null;
                const color = p === 'P0' ? 'border-red-300 bg-red-50' : p === 'P1' ? 'border-amber-300 bg-amber-50' : 'border-gray-300 bg-gray-50';
                return (
                  <div key={p} className={`mt-3 rounded-lg border ${color} p-3`}>
                    <h4 className="mb-2 text-xs font-semibold uppercase">{p} — Open ({items.length})</h4>
                    <ul className="space-y-3">
                      {items.map((t, i) => (
                        <li key={i}>
                          <div className="text-sm font-medium text-gray-800">{t.title}</div>
                          <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-white/70 p-2 font-mono text-xs text-gray-600">{t.body}</pre>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}

              {data.todos.needsResuggest && data.todos.needsResuggest.length > 0 && (
                <div className="mt-3 rounded-lg border border-orange-300 bg-orange-50 p-3">
                  <h4 className="mb-2 text-xs font-semibold uppercase text-orange-700">⚠ Needs re-suggest ({data.todos.needsResuggest.length}) — applier rejected, next Mon's suggest will rewrite</h4>
                  <ul className="space-y-3">
                    {data.todos.needsResuggest.map((t, i) => (
                      <li key={i}>
                        <div className="text-sm font-medium text-gray-800">
                          <span className="mr-2 inline-flex rounded bg-orange-100 px-1.5 py-0.5 text-xs font-semibold text-orange-700">{t.priority} · rejected {t.rejectedDate}</span>
                          {t.title}
                        </div>
                        <div className="mt-1 rounded border border-orange-200 bg-white/70 px-2 py-1 text-xs">
                          <span className="font-semibold text-orange-700">Reason: </span>
                          <span className="text-gray-700">{t.reason}</span>
                        </div>
                        <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-white/70 p-2 font-mono text-xs text-gray-600">{t.body}</pre>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {data.todos.inProgress.length > 0 && (
                <div className="mt-3 rounded-lg border border-blue-300 bg-blue-50 p-3">
                  <h4 className="mb-2 text-xs font-semibold uppercase text-blue-700">In progress ({data.todos.inProgress.length})</h4>
                  <ul className="space-y-3">
                    {data.todos.inProgress.map((t, i) => (
                      <li key={i}>
                        <div className="text-sm font-medium text-gray-800">
                          <span className="mr-2 inline-flex rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-700">{t.priority} · {t.patternId}</span>
                          {t.title}
                        </div>
                        <pre className="mt-1 whitespace-pre-wrap break-words rounded bg-white/70 p-2 font-mono text-xs text-gray-600">{t.body}</pre>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {data.todos.closed.length > 0 && (
                <div className="mt-3 rounded-lg border border-gray-300 bg-gray-50 p-3">
                  <h4 className="mb-2 text-xs font-semibold uppercase text-gray-700">Closed ({data.todos.closed.length})</h4>
                  <ul className="space-y-2">
                    {data.todos.closed.slice(0, 30).map((c, i) => {
                      const outcomeColor = c.outcome === 'IMPROVED' ? 'text-green-700 bg-green-100' :
                                           c.outcome === 'WORSENED' ? 'text-red-700 bg-red-100' :
                                           c.outcome === 'NEUTRAL' ? 'text-gray-600 bg-gray-200' :
                                           'text-gray-500 bg-gray-200';
                      return (
                        <li key={i} className="text-sm">
                          <span className="mr-2 text-xs text-gray-500">{c.date}</span>
                          <span className={`mr-2 inline-flex rounded px-1.5 py-0.5 text-xs font-semibold ${outcomeColor}`}>{c.outcome}</span>
                          {c.patternId && <span className="mr-2 text-xs text-gray-500">({c.patternId})</span>}
                          <span className="text-gray-700">{c.title}</span>
                        </li>
                      );
                    })}
                    {data.todos.closed.length > 30 && <li className="text-xs text-gray-400">… and {data.todos.closed.length - 30} more</li>}
                  </ul>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
